import { useEffect, useRef, useState } from "react";

/**
 * Editorial reveal — a single, restrained fade-up on first viewport entry.
 * Not an "animation". A reveal: 12px lift, opacity 0→1, ~900ms cubic-bezier.
 *
 * Props:
 *   - delay (ms) — stagger within a group
 *   - as — tag (default "div")
 *   - amount — IntersectionObserver threshold (default 0.15)
 *   - once — only reveal once (default true)
 */
export default function Reveal({
  children,
  delay = 0,
  as: Tag = "div",
  amount = 0.15,
  once = true,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect reduced motion preference
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          if (once) obs.disconnect();
        } else if (!once) {
          setShown(false);
        }
      },
      { threshold: amount, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [amount, once]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${shown ? "reveal-in" : ""} ${className}`}
      style={{ transitionDelay: shown ? `${delay}ms` : "0ms" }}
      {...rest}
    >
      {children}
    </Tag>
  );
}
